import {
  BIndexThresholdType,
  useSearchQuestionItemAnalysisYearlyRecordsQuery,
} from '@report/graphql';
import { OnRowOverlayItemAnalysisTable } from '@report/qbank-item-analysis';
import { Table, TableBody, TableContainer, TablePagination } from '@shared/components';
import { TableHeader } from '@shared/table-generator';
import { dayjs } from '@shared/utils';

import { useItemAnalysisYearlySearchParams } from '../../hooks';
import { type MonthAbbreviation } from '../../types';
import { ItemAnalysisYearlyTableBody } from '../item-analysis-yearly-table-body';
import { type ItemAnalysisResponseStatisticsTableBodyProps } from '../item-analysis-yearly-table-body/item-analysis-yearly-table-body';

import { ItemAnalysisYearlyTableHeader } from './item-analysis-yearly-table-header';

export const monthIndexMap: Record<MonthAbbreviation, number> = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

export const monthNamesMap: Record<MonthAbbreviation, string> = {
  JAN: 'ม.ค.',
  FEB: 'ก.พ.',
  MAR: 'มี.ค.',
  APR: 'เม.ย.',
  MAY: 'พ.ค.',
  JUN: 'มิ.ย.',
  JUL: 'ก.ค.',
  AUG: 'ส.ค.',
  SEP: 'ก.ย.',
  OCT: 'ต.ค.',
  NOV: 'พ.ย.',
  DEC: 'ธ.ค.',
};
export default function ItemAnalysisYearlyTable() {
  const [
    {
      page,
      pageSize,
      startMonth,
      endMonth,
      startYear,
      endYear,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
    },
    { changePage, changePageSize },
  ] = useItemAnalysisYearlySearchParams();

  const isFilterSet =
    !!startMonth &&
    !!endMonth &&
    !!startYear &&
    !!endYear &&
    !!examCourse &&
    questionStatus.length > 0 &&
    minExaminees !== null &&
    knowledgeGroupSelection !== null &&
    (knowledgeGroupSelection !== BIndexThresholdType.CustomThreshold ||
      knowledgeGroupCustomThreshold != null) &&
    nonKnowledgeGroupSelection !== null &&
    (nonKnowledgeGroupSelection !== BIndexThresholdType.CustomThreshold ||
      nonKnowledgeGroupCustomThreshold != null);

  const { data: searchData, loading: searchItemAnalysisYearlyLoading } =
    useSearchQuestionItemAnalysisYearlyRecordsQuery({
      variables: {
        searchQuestionItemAnalysisYearlyRecordsInput: {
          startedAt: new Date(
            startYear && startMonth
              ? Date.UTC(startYear, monthIndexMap[startMonth] - 1, 0, 17, 0, 0)
              : '',
          ),
          questionStatus: questionStatus,
          qBankStructureId: examCourse ?? '',
          paging: {
            pageSize: pageSize,
            currentPage: page,
          },
          nonKnowledgeGroup: {
            value: nonKnowledgeGroupCustomThreshold ?? undefined,
            type: nonKnowledgeGroupSelection ?? null,
          },
          minimumExamineePerQuestion: minExaminees ?? 0,
          knowledgeGroup: {
            value: knowledgeGroupCustomThreshold ?? undefined,
            type: knowledgeGroupSelection ?? null,
          },
          endedAt: new Date(
            endYear && endMonth
              ? new Date(Date.UTC(endYear, monthIndexMap[endMonth], 0, 16, 59, 59))
              : '',
          ),
        },
      },
      skip: !isFilterSet,
    });
  const data = searchData?.reportSearchQuestionItemAnalysisYearlyRecords.records;
  const total = searchData?.reportSearchQuestionItemAnalysisYearlyRecords.total ?? 0;

  const rows: ItemAnalysisResponseStatisticsTableBodyProps['rows'] =
    data
      ?.map((record) => ({
        questionRefCode: record.questionRefCode,
        firstPublish: new Date(record.firstPublishedAt),
        lastPublish: new Date(record.lastPublishedAt),
        type: record.calculated ? 'คำนวณ' : 'ไม่คำนวณ',
        status: record.status,
        pInitial: record.pInitial,
        pUsed: record.pUsed,
        bIndex: record.bIndexInitial,
        yearlyData: record.yearlyQuestionItems.map((yearlyItem) => ({
          year: yearlyItem.year,
          examinees: yearlyItem.totalExaminees,
          pCalculated: yearlyItem.pCalculated,
          bIndex: yearlyItem.bIndex,
        })),
        summary: {
          examinees: record.summaryQuestionItem.totalExaminees,
          pCalculated: record.summaryQuestionItem.pCalculated,
          bIndex: record.summaryQuestionItem.bIndex,
        },
      }))
      .sort((a, b) => a.questionRefCode.localeCompare(b.questionRefCode)) ?? [];

  const allYearsSet = new Set<string>();
  for (const row of rows) {
    for (const yearData of row.yearlyData) {
      allYearsSet.add(dayjs().year(yearData.year).format('BBBB'));
    }
  }

  const mapperYearToString = isFilterSet
    ? Array.from(allYearsSet).map((year) => {
        if (dayjs().year(endYear).format('BBBB') === dayjs().year(startYear).format('BBBB')) {
          return `ปี ${year} (${monthNamesMap[startMonth]} - ${monthNamesMap[endMonth]})`;
        }
        if (dayjs().year(endYear).format('BBBB') === year) {
          return `ปี ${year} (ม.ค. - ${monthNamesMap[endMonth]})`;
        }
        if (dayjs().year(startYear).format('BBBB') === year) {
          return `ปี ${year} (${monthNamesMap[startMonth]} - ธ.ค.)`;
        }
        return `ปี ${year} (ม.ค. - ธ.ค.)`;
      })
    : undefined;

  return (
    <TableContainer
      sx={{
        borderRadius: (theme) => theme.shape.rounded(2),
        overflowX: 'auto',
        border: '1px solid',
        borderColor: (theme) => theme.palette.grey[300],
      }}
    >
      <Table
        stickyHeader
        size='small'
        sx={{
          border: 'none',
          borderCollapse: 'initial',
          mb: '-1px',
        }}
      >
        <TableHeader
          columns={ItemAnalysisYearlyTableHeader({ title: mapperYearToString })}
          slotProps={{
            tableHeader: {
              sx: (theme) => ({
                bgcolor: theme.palette.common.black,
                borderColor: theme.palette.grey[300],
                border: 'none',
              }),
            },
            tableHeaderRow: {
              sx: {
                ':first-of-type': {
                  'th:last-of-type': {
                    borderRight: 'none',
                  },
                },
                ':last-of-type > th': {
                  borderTop: '1px solid',
                  borderColor: (theme) => theme.palette.grey[300],
                },
              },
            },
            tableHeaderCell: {
              sx: (theme) => ({
                ...theme.typography.body1Semibold,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.common.black,
                padding: theme.spacing(3),
                borderRight: '1px solid',
                borderBottom: 'none',
                borderColor: theme.palette.grey[300],
              }),
            },
          }}
        />
        {total === 0 ? null : (
          <>
            <TableBody>
              <ItemAnalysisYearlyTableBody rows={rows} />
            </TableBody>
            <TablePagination
              count={total}
              page={page}
              onPageChange={(_, newPage) => changePage(newPage)}
              rowsPerPageOptions={[50, 100, 150, 200]}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => changePageSize(Number(event.target.value))}
            />
          </>
        )}
      </Table>
      <OnRowOverlayItemAnalysisTable
        total={total}
        isLoading={searchItemAnalysisYearlyLoading}
        isFilterSet={isFilterSet}
      />
    </TableContainer>
  );
}
